import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { GitBranch, Loader2, GitBranchPlus, Eye, X } from "lucide-react";
import { authFetch } from "../../utils/authFetch";
import CommitDiffModal from "./CommitDiffModal";

/* ── Types ─────────────────────────────────────────────────────────── */

interface GC {
  id: string;
  parentId: string | null;
  sequence: number;
  message: string | null;
  authorName?: string | null;
  createdAt: string | null;
  changeCount: number;
}
interface GB {
  id: string; name: string; slug: string; headCommitId: string | null;
}
interface Pos { x: number; y: number; row: number; depth: number }

/* ── Palette & layout ──────────────────────────────────────────────── */

const PAL = [
  { dot: "#6366f1", bg: "#eef2ff", text: "#4338ca", line: "#a5b4fc" },
  { dot: "#059669", bg: "#ecfdf5", text: "#065f46", line: "#6ee7b7" },
  { dot: "#d97706", bg: "#fffbeb", text: "#92400e", line: "#fcd34d" },
  { dot: "#7c3aed", bg: "#f5f3ff", text: "#5b21b6", line: "#c4b5fd" },
  { dot: "#0891b2", bg: "#ecfeff", text: "#155e75", line: "#67e8f9" },
  { dot: "#db2777", bg: "#fdf2f8", text: "#9d174d", line: "#f9a8d4" },
  { dot: "#ea580c", bg: "#fff7ed", text: "#9a3412", line: "#fdba74" },
];
const ROW_H = 52;
const COL_W = 48;
const LABEL_W = 140;
const DOT_R = 5;
const HEAD_R = 7;

export default function CommitGraphVisualizer({ projectSlug }: { projectSlug: string }) {
  const [commits, setCommits] = useState<GC[]>([]);
  // The server sends the newest `limit` commits; older ones on request.
  const [limit, setLimit] = useState(1000);
  const [total, setTotal] = useState(0);
  const [branches, setBranches] = useState<GB[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [diffCommit, setDiffCommit] = useState<GC | null>(null);
  const [branchingId, setBranchingId] = useState<string | null>(null);
  const [newBrName, setNewBrName] = useState("");
  const [creating, setCreating] = useState(false);
  const [orphansExpanded, setOrphansExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const fetchGraph = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/commit-graph?limit=${limit}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setCommits(d.commits ?? []); setBranches(d.branches ?? []); setTotal(d.total ?? (d.commits ?? []).length);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load graph");
    } finally { setLoading(false); }
  }, [projectSlug, limit]);

  useEffect(() => { fetchGraph(); }, [fetchGraph]);

  useEffect(() => {
    function h(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setSelectedId(null); setBranchingId(null);
      }
    }
    if (selectedId) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [selectedId]);

  /*
   * Layout: depth-based BFS.
   * - x = depth from root (parent depth + 1). Same depth → same vertical column.
   * - y = row (lane). Main = row 0. Live forks next, orphan rows compacted to bottom.
   * - Edges: same row = horizontal. Cross-row = L-shaped (vertical then horizontal).
   * - No curves, no crossings.
   */
  interface LayoutResult {
    pos: Map<string, Pos>;
    edges: { from: Pos; to: Pos; colorIdx: number }[];
    laneLabels: Map<number, { name: string; colorIdx: number }>;
    svgW: number; svgH: number;
    byId: Map<string, GC>;
    orphanRows: Set<number>;
    orphanCommitIds: Set<string>;
    orphanStartRow: number;
  }

  const layout = useMemo((): LayoutResult | null => {
    if (!commits.length) return null;

    const byId = new Map<string, GC>();
    const children = new Map<string, string[]>();
    for (const c of commits) {
      byId.set(c.id, c);
      if (c.parentId) {
        const arr = children.get(c.parentId);
        if (arr) arr.push(c.id); else children.set(c.parentId, [c.id]);
      }
    }
    for (const [, arr] of children)
      arr.sort((a, b) => (byId.get(a)?.sequence ?? 0) - (byId.get(b)?.sequence ?? 0));

    // Main path: root → main HEAD
    const mainBr = branches.find(b => b.name === "main");
    const mainPath = new Set<string>();
    if (mainBr?.headCommitId) {
      let cur: string | null = mainBr.headCommitId;
      while (cur) { mainPath.add(cur); cur = byId.get(cur)?.parentId ?? null; }
    }

    // Build branch paths: for each non-main branch, walk from HEAD back to
    // where it meets the main path (or another branch). This tells us which
    // commits belong to which branch for lane assignment.
    const commitToBranch = new Map<string, string>(); // commitId → branch name
    const branchPaths = new Map<string, Set<string>>(); // branch name → commit IDs

    // Main gets priority
    for (const id of mainPath) commitToBranch.set(id, "main");
    branchPaths.set("main", mainPath);

    // Non-main branches: walk backwards, claim commits not yet claimed
    const nonMainBranches = branches.filter(b => b.name !== "main" && b.headCommitId);
    // Sort by sequence descending so newer branches get priority for their unique commits
    nonMainBranches.sort((a, b) => {
      const seqA = byId.get(a.headCommitId!)?.sequence ?? 0;
      const seqB = byId.get(b.headCommitId!)?.sequence ?? 0;
      return seqB - seqA;
    });

    for (const br of nonMainBranches) {
      const path = new Set<string>();
      let cur: string | null = br.headCommitId!;
      while (cur && !commitToBranch.has(cur)) {
        path.add(cur);
        commitToBranch.set(cur, br.name);
        cur = byId.get(cur)?.parentId ?? null;
      }
      branchPaths.set(br.name, path);
    }

    const roots = commits.filter(c => !c.parentId || !byId.has(c.parentId))
      .sort((a, b) => a.sequence - b.sequence);

    // Compute depth for every commit (BFS from roots)
    const depth = new Map<string, number>();
    const bfsQueue: string[] = [];
    for (const r of roots) { depth.set(r.id, 0); bfsQueue.push(r.id); }
    let qi = 0;
    while (qi < bfsQueue.length) {
      const id = bfsQueue[qi++];
      const d = depth.get(id)!;
      for (const kid of children.get(id) ?? []) {
        if (!depth.has(kid)) { depth.set(kid, d + 1); bfsQueue.push(kid); }
      }
    }

    // Determine which commits are reachable from branch heads (live) vs orphaned
    const branchReachable = new Set<string>();
    for (const b of branches) {
      if (!b.headCommitId) continue;
      let cur: string | null = b.headCommitId;
      while (cur && !branchReachable.has(cur)) {
        branchReachable.add(cur);
        cur = byId.get(cur)?.parentId ?? null;
      }
    }
    const orphanCommitIds = new Set<string>();
    for (const c of commits) {
      if (!branchReachable.has(c.id)) orphanCommitIds.add(c.id);
    }

    // Assign rows based on branch ownership
    // Row 0 = main. Each other branch gets its own row.
    const branchToRow = new Map<string, number>();
    branchToRow.set("main", 0);
    let nextRow = 1;

    // Assign rows to non-main branches in a stable order
    for (const br of nonMainBranches) {
      if (!branchToRow.has(br.name)) {
        branchToRow.set(br.name, nextRow++);
      }
    }

    // DFS to assign rows — use branch ownership when available, fall back to fork-based assignment
    const tempRow = new Map<string, number>();
    const tempLaneLabels = new Map<number, { name: string; colorIdx: number }>();
    if (mainBr) tempLaneLabels.set(0, { name: mainBr.name, colorIdx: 0 });

    for (const [brName, row] of branchToRow) {
      if (brName !== "main") {
        tempLaneLabels.set(row, { name: brName, colorIdx: row % PAL.length });
      }
    }

    function dfsAssign(id: string, parentRow: number) {
      if (tempRow.has(id)) return;

      // Determine row: use branch ownership if available
      const brName = commitToBranch.get(id);
      let row: number;
      if (brName && branchToRow.has(brName)) {
        row = branchToRow.get(brName)!;
      } else if (orphanCommitIds.has(id)) {
        // Orphan: assign a new row
        row = nextRow++;
        tempLaneLabels.set(row, { name: "orphan", colorIdx: row % PAL.length });
      } else {
        // Unclaimed commit (shared ancestor) — stays on parent's row
        row = parentRow;
      }

      tempRow.set(id, row);

      const kids = children.get(id);
      if (!kids?.length) return;

      // Process children: main-path child first (stays on same row), then others
      const mainKid = kids.find(k => mainPath.has(k));
      for (const kid of kids) {
        if (kid === mainKid) {
          dfsAssign(kid, row);
        } else {
          dfsAssign(kid, row);
        }
      }
    }

    for (const root of roots) {
      const row = mainPath.has(root.id) ? 0 : (branchToRow.get(commitToBranch.get(root.id) || "") ?? nextRow++);
      if (!mainPath.has(root.id) && !tempLaneLabels.has(row)) {
        const brName = commitToBranch.get(root.id) || "branch";
        tempLaneLabels.set(row, { name: brName, colorIdx: row % PAL.length });
      }
      dfsAssign(root.id, row);
    }

    // Identify orphan rows
    const rowCommitSets = new Map<number, string[]>();
    for (const [id, r] of tempRow) {
      const arr = rowCommitSets.get(r);
      if (arr) arr.push(id); else rowCommitSets.set(r, [id]);
    }
    const orphanTempRows = new Set<number>();
    for (const [row, ids] of rowCommitSets) {
      if (ids.every(id => orphanCommitIds.has(id))) orphanTempRows.add(row);
    }

    // Remap rows: live rows first (compacted), then orphan rows at bottom
    const liveRows = Array.from(rowCommitSets.keys()).filter(r => !orphanTempRows.has(r)).sort((a, b) => a - b);
    const orphanRowsSorted = Array.from(orphanTempRows).sort((a, b) => a - b);
    const rowRemap = new Map<number, number>();
    let finalRow = 0;
    for (const r of liveRows) rowRemap.set(r, finalRow++);
    const orphanStartRow = finalRow;
    for (const r of orphanRowsSorted) rowRemap.set(r, finalRow++);

    // Build final positions and lane labels
    const pos = new Map<string, Pos>();
    const laneLabels = new Map<number, { name: string; colorIdx: number }>();
    for (const [oldRow, label] of tempLaneLabels) {
      const newRow = rowRemap.get(oldRow) ?? oldRow;
      laneLabels.set(newRow, label);
    }
    for (const [id, oldRow] of tempRow) {
      const newRow = rowRemap.get(oldRow) ?? oldRow;
      const d = depth.get(id) ?? 0;
      pos.set(id, { x: LABEL_W + d * COL_W + COL_W / 2, y: newRow * ROW_H + ROW_H / 2, row: newRow, depth: d });
    }

    // Final orphan row set (in new numbering)
    const orphanRows = new Set<number>();
    for (const r of orphanRowsSorted) orphanRows.add(rowRemap.get(r)!);

    const maxDepth = Math.max(...Array.from(depth.values()), 0);
    const totalRows = Math.max(finalRow, 1);
    const svgW = LABEL_W + (maxDepth + 1) * COL_W + 40;
    const svgH = totalRows * ROW_H;

    // Edges
    const edges: { from: Pos; to: Pos; colorIdx: number }[] = [];
    for (const c of commits) {
      if (!c.parentId) continue;
      const f = pos.get(c.parentId), t = pos.get(c.id);
      if (!f || !t) continue;
      edges.push({ from: f, to: t, colorIdx: t.row % PAL.length });
    }

    return { pos, edges, laneLabels, svgW, svgH, byId, orphanRows, orphanCommitIds, orphanStartRow };
  }, [commits, branches]);

  /* ── Find which branch owns a commit (walk up to find a branch head) ── */

  const branchForCommit = useCallback((commitId: string): string | undefined => {
    if (!layout) return undefined;
    // Walk from each branch head backwards to see if commitId is reachable
    for (const b of branches) {
      if (!b.headCommitId) continue;
      let cur: string | null = b.headCommitId;
      const visited = new Set<string>();
      while (cur && !visited.has(cur)) {
        visited.add(cur);
        if (cur === commitId) return b.id;
        cur = layout.byId.get(cur)?.parentId ?? null;
      }
    }
    // Orphan commit — use main as source branch (backend just needs any valid branch)
    return branches.find(b => b.name === "main")?.id;
  }, [layout, branches]);

  /* ── Branch from here ──────────────────────────────────────────── */

  const handleBranchFromHere = useCallback(async (commitId: string) => {
    const name = newBrName.trim().toLowerCase();
    if (!name) return;
    setCreating(true);
    try {
      const brId = branchForCommit(commitId);
      const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, sourceBranchId: brId, fromCommitId: commitId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || `Error ${res.status}`);
        return;
      }
      setBranchingId(null); setNewBrName(""); setSelectedId(null);
      fetchGraph();
    } catch { alert("Failed to create branch"); }
    finally { setCreating(false); }
  }, [newBrName, branchForCommit, projectSlug, fetchGraph]);

  const selected = selectedId && layout ? layout.byId.get(selectedId) ?? null : null;
  const selectedPos = selectedId && layout ? layout.pos.get(selectedId) : undefined;
  const headSet = useMemo(() => new Set(branches.map(b => b.headCommitId).filter(Boolean)), [branches]);


  /* ── Render ─────────────────────────────────────────────────────── */

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400 gap-2">
      <Loader2 className="size-4 animate-spin" /> Loading commit graph…
    </div>
  );
  if (error) return (
    <div className="flex items-center justify-center h-64 text-red-500 text-sm">{error}</div>
  );
  if (!layout || !commits.length) return (
    <div className="flex items-center justify-center h-64 text-slate-400 text-sm">No commits yet</div>
  );

  const { pos, edges, laneLabels, svgW, svgH, orphanRows, orphanCommitIds, orphanStartRow } = layout;

  // Single coordinate space — no gap. Orphan rows sit directly below live rows.
  // The divider is a thin line with an overlaid toggle button, not extra height.
  const hasOrphans = orphanRows.size > 0;
  const liveH = orphanStartRow * ROW_H;

  const totalH = orphansExpanded ? svgH : liveH;

  // yFor / topFor: direct row-to-pixel mapping, no offset gap
  const yFor = (row: number) => row * ROW_H + ROW_H / 2;
  const topFor = (row: number) => row * ROW_H;

  // Helper: should this commit be visible?
  const isVisible = (cId: string) => !orphanCommitIds.has(cId) || orphansExpanded;

  return (
    <>
      {total > commits.length && (
        <div className="mb-2 flex items-center gap-2 text-[12px] text-slate-500">
          <span>Showing the latest {commits.length.toLocaleString()} of {total.toLocaleString()} commits.</span>
          <button
            onClick={() => setLimit(l => Math.min(l * 2, 5000))}
            disabled={limit >= 5000}
            className="font-medium text-indigo-600 hover:text-indigo-700 disabled:text-slate-400"
          >
            {limit >= 5000 ? "Older commits: see Commits" : "Load older"}
          </button>
        </div>
      )}
      <div ref={scrollRef} className="overflow-auto border border-slate-200 rounded-lg bg-white" style={{ maxHeight: "70vh" }}>
        <div className="relative" style={{ width: svgW, minHeight: totalH }}>

          {/* Single unified SVG */}
          <svg width={svgW} height={totalH} className="absolute inset-0 pointer-events-none">
            {/* Live row stripes */}
            {Array.from({ length: orphanStartRow }, (_, i) => (
              <rect key={`lr-${i}`} x={0} y={i * ROW_H} width={svgW} height={ROW_H}
                fill={i % 2 === 0 ? "#f8fafc" : "#ffffff"} />
            ))}

            {/* Orphan area background (when expanded) */}
            {orphansExpanded && hasOrphans && (
              <>
                <rect x={0} y={liveH} width={svgW} height={svgH - liveH} fill="#f5f5f4" />
                {Array.from(orphanRows).sort((a, b) => a - b).map((row, i) => (
                  <rect key={`or-${row}`} x={0} y={topFor(row)} width={svgW} height={ROW_H}
                    fill={i % 2 === 0 ? "rgba(120,113,108,0.04)" : "transparent"} />
                ))}
              </>
            )}

            {/* All edges in one pass */}
            {edges.map((e, i) => {
              const fromOrphan = orphanRows.has(e.from.row);
              const toOrphan = orphanRows.has(e.to.row);
              if ((fromOrphan || toOrphan) && !orphansExpanded) return null;
              const pal = PAL[e.colorIdx % PAL.length];
              const fy = yFor(e.from.row);
              const ty = yFor(e.to.row);
              const isOrphanEdge = fromOrphan || toOrphan;
              const op = isOrphanEdge ? 0.4 : 1;
              if (e.from.row === e.to.row) {
                return <line key={i} x1={e.from.x} y1={fy} x2={e.to.x} y2={ty}
                  stroke={pal.line} strokeWidth={2} opacity={op} />;
              }
              return (
                <g key={i} opacity={op}>
                  <line x1={e.from.x} y1={fy} x2={e.from.x} y2={ty}
                    stroke={pal.line} strokeWidth={1.5} opacity={0.55} />
                  <line x1={e.from.x} y1={ty} x2={e.to.x} y2={ty}
                    stroke={pal.line} strokeWidth={2} />
                </g>
              );
            })}
          </svg>

          {/* Lane labels */}
          {Array.from(laneLabels.entries()).map(([row, { name, colorIdx }]) => {
            const isOrphan = orphanRows.has(row);
            if (isOrphan && !orphansExpanded) return null;
            const pal = PAL[colorIdx % PAL.length];
            return (
              <div
                key={row}
                className="absolute flex items-center gap-1.5 px-2 text-[13px] font-medium rounded-full truncate"
                style={{
                  top: topFor(row) + ROW_H / 2 - 10,
                  left: 6,
                  width: LABEL_W - 12,
                  height: 20,
                  background: pal.bg,
                  color: pal.text,
                  zIndex: 5,
                  opacity: isOrphan ? 0.55 : 1,
                }}
              >
                <GitBranch className="size-3 shrink-0" />
                <span className="truncate">{name}</span>
              </div>
            );
          })}

          {/* All commit dots */}
          {commits.map(c => {
            if (!isVisible(c.id)) return null;
            const p = pos.get(c.id);
            if (!p) return null;
            const isOrphan = orphanCommitIds.has(c.id);
            const isHead = headSet.has(c.id);
            const isSel = selectedId === c.id;
            const pal = PAL[p.row % PAL.length];
            const r = isHead ? HEAD_R : DOT_R;
            const cy = yFor(p.row);
            return (
              <button
                key={c.id}
                className="absolute rounded-full border-2 transition-shadow focus:outline-none"
                style={{
                  left: p.x - r - 2,
                  top: cy - r - 2,
                  width: (r + 2) * 2,
                  height: (r + 2) * 2,
                  background: isHead ? pal.dot : "#fff",
                  borderColor: pal.dot,
                  boxShadow: isSel ? `0 0 0 3px ${pal.line}` : undefined,
                  zIndex: 10,
                  opacity: isOrphan ? 0.5 : 1,
                }}
                title={c.message || `#${c.sequence}`}
                onClick={(e) => {
                  setSelectedId(isSel ? null : c.id); setBranchingId(null);
                  if (!isSel) {
                    const btn = e.currentTarget;
                    requestAnimationFrame(() => {
                      const container = scrollRef.current;
                      if (!container || !btn) return;
                      const popoverBottom = btn.offsetTop + btn.offsetHeight + 160;
                      const visibleBottom = container.scrollTop + container.clientHeight;
                      if (popoverBottom > visibleBottom)
                        container.scrollTo({ top: popoverBottom - container.clientHeight + 20, behavior: "smooth" });
                      const popoverRight = btn.offsetLeft + 260;
                      const visibleRight = container.scrollLeft + container.clientWidth;
                      if (popoverRight > visibleRight)
                        container.scrollTo({ left: popoverRight - container.clientWidth + 20, behavior: "smooth" });
                    });
                  }
                }}
              />
            );
          })}

          {/* Divider line + toggle overlaid at boundary */}
          {hasOrphans && (
            <div
              className="absolute left-0 right-0 flex items-center"
              style={{ top: liveH - 1, height: 0, zIndex: 12 }}
            >
              <div className="absolute inset-x-0 top-0 h-px bg-slate-300" />
              <button
                onClick={() => setOrphansExpanded(v => !v)}
                className="absolute left-2 flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[13px] font-medium border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 shadow-sm transition-colors"
                style={{ top: -12 }}
              >
                <svg width="8" height="8" viewBox="0 0 8 8" className="shrink-0">
                  {orphansExpanded
                    ? <path d="M1 2.5 L4 5.5 L7 2.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    : <path d="M2.5 1 L5.5 4 L2.5 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  }
                </svg>
                {orphanCommitIds.size} orphaned
              </button>
            </div>
          )}

          {/* Popover card */}
          {selected && selectedPos && (() => {
            const isOrphan = orphanCommitIds.has(selected.id);
            if (isOrphan && !orphansExpanded) return null;
            const popY = yFor(selectedPos.row) + HEAD_R + 10;
            return (
              <div
                ref={popoverRef}
                className="absolute bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-20"
                style={{ left: selectedPos.x - 130 + DOT_R, top: popY, width: 260 }}
              >
                <div className="flex items-start justify-between mb-1.5">
                  <span className="text-[14px] font-semibold text-slate-800 leading-tight line-clamp-2">
                    {selected.message || "(no message)"}
                  </span>
                  <button onClick={() => setSelectedId(null)} className="p-0.5 rounded hover:bg-slate-100 shrink-0 ml-1">
                    <X className="size-3 text-slate-400" />
                  </button>
                </div>
                <div className="text-[12px] text-slate-400 mb-2 flex gap-2">
                  <span>#{selected.sequence}</span>
                  {selected.authorName && <span>{selected.authorName}</span>}
                  {selected.createdAt && <span>{new Date(selected.createdAt).toLocaleDateString()}</span>}
                  {selected.changeCount > 0 && <span>{selected.changeCount} changes</span>}
                </div>
                <div className="flex gap-1.5">
                  <button
                    className="flex items-center gap-1 px-2 py-1 text-[13px] rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                    onClick={() => setDiffCommit(selected)}
                  >
                    <Eye className="size-3" /> View Diff
                  </button>
                  {branchingId === selected.id ? (
                    <form className="flex items-center gap-1" onSubmit={e => { e.preventDefault(); handleBranchFromHere(selected.id); }}>
                      <input
                        autoFocus
                        className="w-[100px] px-1.5 py-0.5 text-[13px] border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        placeholder="branch-name"
                        value={newBrName}
                        onChange={e => setNewBrName(e.target.value)}
                        disabled={creating}
                      />
                      <button type="submit" disabled={creating || !newBrName.trim()}
                        className="px-1.5 py-0.5 text-[13px] rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors">
                        {creating ? "…" : "Go"}
                      </button>
                    </form>
                  ) : (
                    <button
                      className="flex items-center gap-1 px-2 py-1 text-[13px] rounded bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
                      onClick={() => { setBranchingId(selected.id); setNewBrName(""); }}
                    >
                      <GitBranchPlus className="size-3" /> Branch
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Diff modal */}
      {diffCommit && (
        <CommitDiffModal
          commit={{
            id: diffCommit.id,
            sequence: diffCommit.sequence,
            parentId: diffCommit.parentId,
            message: diffCommit.message,
            authorName: diffCommit.authorName,
            createdAt: diffCommit.createdAt,
          }}
          parentCommitId={diffCommit.parentId ?? null}
          projectSlug={projectSlug}
          branchId={branchForCommit(diffCommit.id) ?? ""}
          onClose={() => setDiffCommit(null)}
        />
      )}
    </>
  );
}
